import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { Platform } from "react-native";
import { useReducedMotion } from "react-native-reanimated";

import { H1 } from "../Typography";
import BlinkingCursor from "./BlinkingCursor";

const TIME_TO_TYPE = 1000;
const TIME_TO_UNTYPE = 500;
const DELAY_AFTER_TYPED = 1000;
const DELAY_AFTER_UNTYPED = 500;
const ANIMATION_INTERVAL =
  TIME_TO_TYPE + TIME_TO_UNTYPE + DELAY_AFTER_TYPED + DELAY_AFTER_UNTYPED;

const SuggestionTyper = ({
  suggestions,
  suggestedQueryIdx,
  setSuggestedQueryIdx,
}: {
  suggestions: string[];
  suggestedQueryIdx: number;
  setSuggestedQueryIdx: Dispatch<SetStateAction<number>>;
}) => {
  const [forward, setForward] = useState(true);
  const [text, setText] = useState("");
  const reducedMotion = useReducedMotion();

  const currentSuggestion = suggestions[suggestedQueryIdx] || "";

  useEffect(() => {
    if (!suggestions?.length || reducedMotion) return;

    const interval = setInterval(() => {
      setSuggestedQueryIdx((prev) => (prev + 1) % suggestions.length);
    }, ANIMATION_INTERVAL);
    return () => clearInterval(interval);
  }, [suggestions, reducedMotion, setSuggestedQueryIdx]);

  useEffect(() => {
    if (reducedMotion) {
      setText(currentSuggestion);
      return;
    }

    let interval: ReturnType<typeof setInterval>;
    if (forward) {
      // Need separate intervals because typing and untyping happen at different speeds
      interval = setInterval(() => {
        setText((typed) => {
          if (typed.length < currentSuggestion.length) {
            return currentSuggestion.slice(0, typed.length + 1);
          } else {
            setTimeout(() => {
              setForward(false);
            }, DELAY_AFTER_TYPED);
            return typed;
          }
        });
      }, TIME_TO_TYPE / Math.max(currentSuggestion.length, 1));
    } else {
      interval = setInterval(() => {
        setText((typed) => {
          if (typed.length === 0) return "";
          return currentSuggestion.slice(0, typed.length - 1);
        });
      }, TIME_TO_UNTYPE / Math.max(currentSuggestion.length, 1));
    }

    return () => clearInterval(interval);
  }, [currentSuggestion, reducedMotion, forward]);

  return (
    <H1
      textAlign="center"
      {...(Platform.OS === "web" ? { translate: "no" } : {})}
    >
      {text}
      <BlinkingCursor />
    </H1>
  );
};

// We wrap SuggestionTyper with this controller to easily reset its state via key.
// This spares us the complexity of timing after the untyping animation is complete.
const SuggestionController = ({ suggestions }: { suggestions: string[] }) => {
  const [suggestedQueryIdx, setSuggestedQueryIdx] = useState<number>(0);
  return (
    <SuggestionTyper
      key={suggestedQueryIdx}
      suggestions={suggestions}
      suggestedQueryIdx={suggestedQueryIdx}
      setSuggestedQueryIdx={setSuggestedQueryIdx}
    />
  );
};

export default SuggestionController;
